import { type CSSProperties, useCallback, useEffect, useState } from 'react';
import { usePowerSyncDb } from '../../adapters/powersync';
import { useInitialSyncMilestones } from '../../sync/useInitialSyncMilestones';
import { useUploadQueueStats } from '../../sync/useUploadQueueStats';
import { retrySync, forceResync } from '../../sync/syncActions';

// ─── Types ───

interface SyncInfo {
  connected: boolean;
  connecting: boolean;
  lastSyncedAt: string | null;
  hasSynced: boolean;
  downloading: boolean;
  uploading: boolean;
  downloadError: string | null;
  uploadError: string | null;
}

// ─── Styles ───

const pageStyle: CSSProperties = {
  padding: 'var(--sp-8, 32px)',
  maxWidth: 640,
  height: '100%',
  overflowY: 'auto',
};

const sectionStyle: CSSProperties = {
  padding: 'var(--sp-5, 20px)',
  background: 'var(--bg-surface, #fff)',
  border: '1px solid var(--border, rgba(15,23,42,0.12))',
  borderRadius: 'var(--radius-lg, 14px)',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-3, 12px)',
};

const sectionTitle: CSSProperties = {
  fontSize: 'var(--font-size-sm, 14px)',
  fontWeight: 'var(--font-weight-semi, 600)',
  color: 'var(--text, #0f172a)',
  margin: 0,
};

const rowStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  fontSize: 'var(--font-size-sm, 14px)',
};

const labelStyle: CSSProperties = {
  color: 'var(--text-muted, #64748b)',
};

const valueStyle: CSSProperties = {
  fontWeight: 'var(--font-weight-medium, 500)',
  color: 'var(--text, #0f172a)',
  fontVariantNumeric: 'tabular-nums',
};

// ─── Status Badge ───

function StatusBadge({ connected }: { connected: boolean }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '2px 10px',
        borderRadius: 'var(--radius-full, 9999px)',
        fontSize: 'var(--font-size-xs, 12px)',
        fontWeight: 'var(--font-weight-medium, 500)',
        background: connected ? '#f0fdf4' : '#fef2f2',
        color: connected ? '#16a34a' : '#dc2626',
        border: `1px solid ${connected ? '#bbf7d0' : '#fecaca'}`,
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: connected ? '#16a34a' : '#dc2626',
        }}
      />
      {connected ? 'Connected' : 'Disconnected'}
    </span>
  );
}

// ─── Page Component ───

export function SyncStatusPage() {
  const { db, connector, isConnected } = usePowerSyncDb();
  const milestones = useInitialSyncMilestones();
  const uploadQueue = useUploadQueueStats();

  const [syncInfo, setSyncInfo] = useState<SyncInfo>({
    connected: false,
    connecting: false,
    lastSyncedAt: null,
    hasSynced: false,
    downloading: false,
    uploading: false,
    downloadError: null,
    uploadError: null,
  });
  const [isRetrying, setIsRetrying] = useState(false);
  const [confirmForce, setConfirmForce] = useState(false);

  // Poll sync status
  useEffect(() => {
    if (!db) return;

    function update() {
      const s = db!.currentStatus;
      setSyncInfo({
        connected: s?.connected ?? false,
        connecting: s?.connecting ?? false,
        lastSyncedAt: s?.lastSyncedAt ? new Date(s.lastSyncedAt).toLocaleString() : null,
        hasSynced: s?.hasSynced ?? false,
        downloading: s?.dataFlowStatus?.downloading ?? false,
        uploading: s?.dataFlowStatus?.uploading ?? false,
        downloadError:
          s?.connected && s?.hasSynced ? null : (s?.dataFlowStatus?.downloadError?.message ?? null),
        uploadError:
          s?.connected && s?.hasSynced ? null : (s?.dataFlowStatus?.uploadError?.message ?? null),
      });
    }

    update();
    const interval = setInterval(update, 2_000);
    return () => clearInterval(interval);
  }, [db]);

  const handleRetry = useCallback(async () => {
    if (!db || !connector) return;
    setIsRetrying(true);
    try {
      await retrySync(db, connector);
    } catch (err) {
      console.error('[SyncStatus] Retry failed:', err);
    } finally {
      setIsRetrying(false);
    }
  }, [db, connector]);

  const handleForceResync = useCallback(async () => {
    if (!db) return;
    if (uploadQueue.hasPending && !confirmForce) {
      setConfirmForce(true);
      return;
    }
    setIsRetrying(true);
    try {
      await forceResync(db);
    } catch (err) {
      console.error('[SyncStatus] Force resync failed:', err);
      setIsRetrying(false);
    }
  }, [db, uploadQueue.hasPending, confirmForce]);

  return (
    <div style={pageStyle}>
      <h1
        style={{
          fontSize: 'var(--font-size-xl, 20px)',
          fontWeight: 'var(--font-weight-semi, 600)',
          color: 'var(--text, #0f172a)',
          margin: '0 0 var(--sp-6, 24px)',
        }}
      >
        Sync Status
      </h1>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-5, 20px)' }}>
        {/* Connection Status */}
        <div style={sectionStyle}>
          <h2 style={sectionTitle}>Connection</h2>
          <div style={rowStyle}>
            <span style={labelStyle}>Status</span>
            <StatusBadge connected={isConnected} />
          </div>
          <div style={rowStyle}>
            <span style={labelStyle}>Last synced</span>
            <span style={valueStyle}>{syncInfo.lastSyncedAt ?? 'Never'}</span>
          </div>
          <div style={rowStyle}>
            <span style={labelStyle}>Downloading</span>
            <span style={valueStyle}>{syncInfo.downloading ? 'Yes' : 'No'}</span>
          </div>
          <div style={rowStyle}>
            <span style={labelStyle}>Uploading</span>
            <span style={valueStyle}>{syncInfo.uploading ? 'Yes' : 'No'}</span>
          </div>
          {syncInfo.downloadError && (
            <div
              style={{
                fontSize: 'var(--font-size-xs, 12px)',
                color: '#dc2626',
                background: '#fef2f2',
                padding: 'var(--sp-2, 8px)',
                borderRadius: 'var(--radius-md, 10px)',
              }}
            >
              Download error: {syncInfo.downloadError}
            </div>
          )}
          {syncInfo.uploadError && (
            <div
              style={{
                fontSize: 'var(--font-size-xs, 12px)',
                color: '#dc2626',
                background: '#fef2f2',
                padding: 'var(--sp-2, 8px)',
                borderRadius: 'var(--radius-md, 10px)',
              }}
            >
              Upload error: {syncInfo.uploadError}
            </div>
          )}
        </div>

        {/* Upload Queue */}
        <div style={sectionStyle}>
          <h2 style={sectionTitle}>Upload Queue</h2>
          <div style={rowStyle}>
            <span style={labelStyle}>Pending operations</span>
            <span style={valueStyle}>{uploadQueue.count}</span>
          </div>
        </div>

        {/* Milestones */}
        <div style={sectionStyle}>
          <h2 style={sectionTitle}>Data Milestones</h2>
          {milestones.milestones.map((m) => (
            <div key={m.key} style={rowStyle}>
              <span style={labelStyle}>
                {m.done ? '\u2705' : '\u23F3'} {m.label}
              </span>
              <span style={valueStyle}>{m.count} rows</span>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div style={sectionStyle}>
          <h2 style={sectionTitle}>Actions</h2>

          {confirmForce && uploadQueue.hasPending && (
            <div
              style={{
                padding: 'var(--sp-3, 12px)',
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: 'var(--radius-md, 10px)',
                fontSize: 'var(--font-size-xs, 12px)',
                color: '#991b1b',
              }}
            >
              You have {uploadQueue.count} pending upload(s) that will be lost. Click Force Resync
              again to confirm.
            </div>
          )}

          <div style={{ display: 'flex', gap: 'var(--sp-3, 12px)' }}>
            <button
              onClick={handleRetry}
              disabled={isRetrying}
              style={{
                padding: '10px 20px',
                borderRadius: 'var(--radius-md, 10px)',
                border: '1px solid var(--border, rgba(15,23,42,0.12))',
                background: 'var(--bg-surface, #fff)',
                fontSize: 'var(--font-size-sm, 14px)',
                fontWeight: 'var(--font-weight-medium, 500)',
                color: 'var(--text, #0f172a)',
                cursor: isRetrying ? 'not-allowed' : 'pointer',
                opacity: isRetrying ? 0.5 : 1,
              }}
            >
              {isRetrying ? 'Retrying...' : 'Retry Sync'}
            </button>
            <button
              onClick={handleForceResync}
              disabled={isRetrying}
              style={{
                padding: '10px 20px',
                borderRadius: 'var(--radius-md, 10px)',
                border: '1px solid #fecaca',
                background: '#fef2f2',
                fontSize: 'var(--font-size-sm, 14px)',
                fontWeight: 'var(--font-weight-medium, 500)',
                color: '#dc2626',
                cursor: isRetrying ? 'not-allowed' : 'pointer',
                opacity: isRetrying ? 0.5 : 1,
              }}
            >
              {isRetrying ? 'Clearing...' : 'Force Resync'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
